import React from 'react';
import PropTypes from 'prop-types';

/**
 * PageHeaderActionsContext — lets a view inject action controls (e.g. an Add
 * button) into the single shared AriesPageHeader title row without rendering a
 * second header or serializing a React node/handler through redux.
 *
 * The app shell provides the context (around the content area) and the single
 * AriesPageHeader instance consumes `actions`. A view registers/clears its node
 * with `usePageHeaderActions(node, deps)`.
 */
const PageHeaderActionsContext = React.createContext({
  actions: null,
  setActions: () => {},
});

export const PageHeaderActionsProvider = ({ children }) => {
  const [actions, setActions] = React.useState(null);
  const value = React.useMemo(() => ({ actions, setActions }), [actions]);
  return (
    <PageHeaderActionsContext.Provider value={value}>
      {children}
    </PageHeaderActionsContext.Provider>
  );
};

PageHeaderActionsProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Register page-header actions for the current view. Pass a node (or
 * `{ node, position }`) and a deps array; the actions are set on mount/changes
 * and cleared on unmount.
 */
export const usePageHeaderActions = (node, deps = []) => {
  const { setActions } = React.useContext(PageHeaderActionsContext);
  React.useEffect(() => {
    setActions(node ?? null);
    return () => setActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export default PageHeaderActionsContext;
